import { ChevronRight, Clock, Plus } from 'lucide-react';
import { useState } from 'react';

import { CapabilityGate } from '../../../../components/CapabilityGate';
import { isTerminalStatus } from '../../format';
import { useChecklists } from '../../hooks/use-checklists';
import {
  useAppointments,
  useEpisodeCandidates,
  usePendencies,
  usePersonWaitlist,
  useSetEpisodeFocus,
} from '../../hooks/use-panel-fixtures';
import { ChecklistBlock } from '../ChecklistBlock';
import { ConversationBookingModal } from '../ConversationBookingModal';

import { AppointmentBlock } from './AppointmentBlock';
import { InlineRegisterRow } from './InlineRegisterRow';
import { PanelSection } from './PanelSection';
import { PendencyBlock } from './PendencyBlock';
import { WaitlistBlock } from './WaitlistBlock';

import type { ConversationDetail } from '../../../../lib/types';
import type { AppointmentFixture } from '../../hooks/use-panel-fixtures';

/**
 * Aba "Ações" — o que ficou pendurado quando a conversa acabar.
 *
 * **Agrupada por TEMPO, não por tipo** (spec `2026-08-14-aba-acoes-conversa-em-curso-design.md`
 * §5.5; mockup `1a`+`2a` do Claude Design). A disposição ensina a fronteira que a tela
 * não mostrava:
 *
 *   ANTES DE ENCERRAR  → Checklist. É o único que TRAVA o fechamento
 *                        (`ClosureBlockersService` o consulta; pendência e agendamento
 *                        não estão na lista, e não devem estar).
 *   DEPOIS DE ENCERRAR → Compromissos · Agendamentos · Fila de espera · Pendências.
 *                        Tudo isso SOBREVIVE à conversa — é o motivo de existirem.
 *
 * **Seção sem item não aparece**, mesmo padrão que a Fila de espera já usava. Antes, uma
 * conversa nova mostrava três estados vazios de seção mais um disclaimer global: quatro
 * frases dizendo que não havia nada. Agora a aba mostra o que existe, e quando não há
 * nada diz isso uma vez só.
 *
 * **Compromissos × Pendências são a MESMA entidade**, separadas pelo `source`: promessa
 * (do operador ou do cliente) × trabalho que não nasceu de promessa. A seção
 * «Compromissos do cliente» (ADR-105 `CustomerCommitment`) saiu — ela era estruturalmente
 * vazia porque nada no motor cria a entidade, e a promessa do cliente virou uma etiqueta
 * dentro da Pendency (§5.3 da spec; ausência vigiada em `absence-claims.ts`).
 */
export function TabActions({ conversation }: { conversation: ConversationDetail }): JSX.Element {
  const { items: appointments } = useAppointments(conversation);
  const { items: pendencies } = usePendencies(conversation);
  const { items: waitlist } = usePersonWaitlist(conversation);
  const { checklists } = useChecklists(conversation.id);

  // §2.8 — em conversa terminal os blocos do painel direito ficam read-only.
  const isTerminal = isTerminalStatus(conversation.status);

  // ADR-155 Fase 4 (§3.1) — episódio em foco desta conversa (v1 só 'appointment').
  const focusedEpisodeId =
    conversation.episodeRefType === 'appointment' ? (conversation.episodeRefId ?? null) : null;
  const setFocus = useSetEpisodeFocus(conversation.id);

  const [bookingOpen, setBookingOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);

  const hasChecklist = checklists.length > 0;
  const hasAfterItems =
    pendencies.length + appointments.length + waitlist.length > 0 || registerOpen;
  /**
   * O grupo «depois de encerrar» também existe quando ainda não há NADA nele, desde
   * que a conversa esteja viva — porque é onde mora a única porta de registro da aba.
   *
   * Achado na validação interativa de 14/Ago: numa conversa com checklist e sem
   * compromisso, esconder as seções vazias tinha escondido junto o `+ registrar` (que
   * vive no cabeçalho da seção Compromissos), e o botão do estado vazio só aparece
   * quando TUDO, inclusive o checklist, está vazio. Resultado: beco sem saída — o
   * operador não tinha como registrar nada pela aba. A porta é uma LINHA, não uma
   * caixa vazia: informa zero e oferece a ação.
   *
   * Só vale quando há checklist ACIMA: com a aba inteiramente vazia quem responde é o
   * estado vazio logo abaixo, que já oferece as duas portas e diz a frase.
   */
  const hasAfter = hasAfterItems || (!isTerminal && hasChecklist);

  // Nada em lugar nenhum: uma frase e duas portas — não quatro frases de vazio.
  if (!hasChecklist && !hasAfter) {
    return (
      <>
        <div className="tab-empty" data-testid="actions-empty">
          <Clock className="icon" size={20} aria-hidden />
          <span>Nada ficou pendurado nesta conversa.</span>
          {!isTerminal && (
            <div className="flex gap-2">
              <button
                type="button"
                className="btn btn--secondary btn--xs"
                onClick={() => setRegisterOpen(true)}
              >
                + registrar algo
              </button>
              <CapabilityGate capability="scheduling-native">
                <button
                  type="button"
                  className="btn btn--secondary btn--xs"
                  onClick={() => setBookingOpen(true)}
                  data-testid="conversation-agendar-nativo-btn"
                >
                  + agendar
                </button>
              </CapabilityGate>
            </div>
          )}
        </div>
        <ConversationBookingModal
          conversationId={conversation.id}
          personId={conversation.personId}
          bondId={conversation.bondId}
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
        />
      </>
    );
  }

  return (
    <div className="flex flex-col">
      {hasChecklist && (
        <>
          <div className="subgroup-head">Antes de encerrar</div>
          <PanelSection title="Checklist" eyebrow="o que trava o encerramento">
            <ChecklistBlock conversationId={conversation.id} disabled={isTerminal} />
          </PanelSection>
        </>
      )}

      {/* A divisória só aparece se houver seção embaixo dela — divisória órfã é ruído. */}
      {hasAfter && <div className="subgroup-head">Depois de encerrar</div>}

      {(pendencies.length > 0 || registerOpen) && (
        <PanelSection
          title="O que ficou de fazer"
          eyebrow="continua depois daqui"
          count={pendencies.length}
          headerAction={
            isTerminal ? undefined : (
              <button
                type="button"
                /* Componente REAL do DS. Duas tentativas anteriores foram atrás:
                   `btn--add`, que eu inventei e não tinha uma linha de CSS; e
                   `text-2xs text-brand`, que também não existe neste app (nem a
                   escala `2xs` nem a cor `brand` estão no tailwind.config). A
                   segunda até pintava — mas como LINK, e ação de cabeçalho aqui é
                   botão. Terceira cobrança do TL no mesmo ponto, 15/Ago. */
                className="btn btn--secondary btn--xs"
                data-testid="inline-register-toggle"
                onClick={(e) => {
                  // `PanelSection` avisa no docblock: sem isto o clique também
                  // alterna o abrir/fechar da própria seção (bubbling no head).
                  e.stopPropagation();
                  setRegisterOpen((v) => !v);
                }}
              >
                Registrar
              </button>
            )
          }
        >
          {/* A linha entra como PRIMEIRO item do corpo, acima dos cartões (mockup 2a). */}
          {registerOpen && (
            <InlineRegisterRow
              conversationId={conversation.id}
              onDone={() => setRegisterOpen(false)}
            />
          )}
          {pendencies.length > 0 && <PendencyBlock items={pendencies} disabled={isTerminal} />}
        </PanelSection>
      )}

      {appointments.length > 0 && (
        <PanelSection
          title="Agendamentos"
          eyebrow="vinculados a esta conversa"
          count={appointments.length}
          headerAction={
            // ADR-155 Fase 4 (§3.1) — booking NATIVO pela conversa (o foco nasce
            // server-side junto do agendamento). Continua MODAL de propósito: horário,
            // recurso e disponibilidade não cabem no campo único do registro inline.
            <CapabilityGate capability="scheduling-native">
              <button
                type="button"
                className="btn btn--secondary btn--xs"
                onClick={(e) => {
                  e.stopPropagation();
                  setBookingOpen(true);
                }}
                disabled={isTerminal}
                data-testid="conversation-agendar-nativo-btn"
              >
                Agendar
              </button>
            </CapabilityGate>
          }
        >
          <AppointmentBlock
            items={appointments}
            disabled={isTerminal}
            focusedEpisodeId={focusedEpisodeId}
            onFocusHere={(appointmentId) => setFocus.focus(appointmentId)}
            focusPending={setFocus.isPending}
          />

          <OtherClientAppointments
            conversation={conversation}
            excludeIds={new Set(appointments.map((a) => a.id))}
            disabled={isTerminal}
            focusedEpisodeId={focusedEpisodeId}
            onFocusHere={(appointmentId) => setFocus.focus(appointmentId)}
            focusPending={setFocus.isPending}
          />
        </PanelSection>
      )}

      {waitlist.length > 0 && (
        <PanelSection
          title="Fila de espera"
          eyebrow="onde a IA enfileirou este cliente"
          count={waitlist.length}
        >
          <WaitlistBlock items={waitlist} />
        </PanelSection>
      )}

      {/* A porta de registro quando ainda não há compromisso nenhum. Uma linha, não
          uma caixa: a seção vazia era ruído, mas esconder a seção tinha escondido a
          única entrada da aba junto. */}
      {!isTerminal && pendencies.length === 0 && !registerOpen && (
        <button
          type="button"
          className="btn btn--secondary btn--xs"
          style={{ padding: 'var(--space-2) var(--space-1)' }}
          onClick={() => setRegisterOpen(true)}
          data-testid="inline-register-empty-entry"
        >
          <Plus size={12} aria-hidden />
          Registrar o que ficou de fazer
        </button>
      )}

      <ConversationBookingModal
        conversationId={conversation.id}
        personId={conversation.personId}
        bondId={conversation.bondId}
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
      />
    </div>
  );
}

/**
 * ADR-155 Fase 4 (E4/§3.1) — subseção "Outros agendamentos do cliente":
 * candidatos ao foco fora da lista da conversa (mesmo raio de posse — mesma
 * conversa/pessoa/bond).
 *
 * **O expansor só existe se houver o que expandir** (achado do TL, 15/Ago: «tem
 * uma expansão que mostra Outros agendamentos do cliente mas não tem nenhum
 * outro agendamento»). Era a mesma doença que esta rodada passou removendo — uma
 * gaveta que se anuncia e está vazia.
 *
 * O fetch continua GATEADO, só que por outro critério: dispara quando a seção de
 * Agendamentos renderiza — isto é, quando a conversa JÁ tem agendamento. Conversa
 * sem agendamento nenhum (a maioria) não paga query. É uma query a mais só onde a
 * pergunta faz sentido, em troca de nunca prometer conteúdo inexistente.
 */
function OtherClientAppointments({
  conversation,
  excludeIds,
  disabled,
  focusedEpisodeId,
  onFocusHere,
  focusPending,
}: {
  conversation: ConversationDetail;
  excludeIds: ReadonlySet<string>;
  disabled: boolean;
  focusedEpisodeId: string | null;
  onFocusHere: (appointmentId: string) => void;
  focusPending: boolean;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const { items } = useEpisodeCandidates(conversation, true);
  const filtered: AppointmentFixture[] = items.filter((a) => !excludeIds.has(a.id));

  // Nada para expandir ⇒ nem o expansor aparece.
  if (filtered.length === 0) return null;

  return (
    <div
      className="mt-2 border-t pt-2"
      style={{ borderColor: 'var(--border-subtle)' }}
      data-testid="other-client-appointments"
    >
      <button
        type="button"
        className="flex items-center gap-1 text-xs font-medium"
        style={{ color: 'var(--fg-3)', background: 'none', border: 'none', cursor: 'pointer' }}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="other-client-appointments-toggle"
      >
        <ChevronRight
          size={12}
          aria-hidden
          style={{
            transform: open ? 'rotate(90deg)' : undefined,
            transition: 'transform var(--dur-fast, 120ms) var(--ease-out, ease)',
          }}
        />
        Outros agendamentos do cliente ({filtered.length})
      </button>
      {open && (
        <div className="mt-1">
          <AppointmentBlock
            items={filtered}
            disabled={disabled}
            focusedEpisodeId={focusedEpisodeId}
            onFocusHere={onFocusHere}
            focusPending={focusPending}
          />
        </div>
      )}
    </div>
  );
}
